<!--
  DateTimeRange Component Principles and Rules
  
  Core Principles (Priority Order):
  1. Preserve User Intent
     - The value user is actively changing must be preserved exactly as entered
     - Any necessary adjustments should only affect other fields
     - The focused/changed input takes precedence

  2. Maintain Valid Time Range
     - End must always be after start
     - Adjustments should maintain this rule while following principle #1

  3. Minimize Disruption
     - Prefer adjusting times over dates when possible
     - Keep adjustments within the same "group" (start or end)
     - When dates become the same, adjust times rather than dates

  4. Preserve Duration When Possible
     - Remember the previous valid time gap
     - Apply this gap when making adjustments
     - Default to 1-hour gap when no previous gap exists

  Specific Behavior Rules:
  1. When Changing Start Date
     - If end becomes invalid:
       - If same day: Adjust end time forward
       - If different days: Move end date forward maintaining gap

  2. When Changing End Date
     - If start becomes invalid:
       - If same day: Adjust end time forward
       - If different days: Keep end date and adjust start date backward

  3. When Changing Start Time
     - If end becomes invalid:
       - Always adjust end time forward maintaining gap
       - Never modify the start time entered by user

  4. When Changing End Time
     - If start becomes invalid:
       - Always adjust start time backward maintaining gap
       - Never modify the end time entered by user

  5. Visual Feedback
     - Highlight only the fields that were automatically adjusted
     - Don't highlight the field user is actively changing
-->

<script>
  import t from "#lib/i18n.svelte.js";
  import DateInput from "#lib/DateInput.svelte";
  import TimeInput from "#lib/TimeInput.svelte";
  import FormLabel from "#lib/FormLabel.svelte";
  import { 
    convert_local_time_to_utc,
    format_date_range,
    format_utc_time_as_local,
    get_end_of_day,
    localize_utc_date,
    unlocalize_utc_date,
    yyyy_mm_dd_from_date
  } from "#lib/util.js";

  let { 
    /** @type {string} ISO 8601 UTC datetime string */
    starts_at = $bindable(),
    /** @type {string} ISO 8601 UTC datetime string */
    ends_at = $bindable(),
    /** @type {boolean} Whether the event spans entire days */
    all_day = $bindable(),
    /** @type {boolean} Whether the component is in edit mode */
    is_editing,
    /** @type {boolean} Whether to show form labels */
    show_label = true,
    /** @type {'small' | 'medium' | 'large'} Size variant for input fields */
    size = 'small',
    /** @type {string} Visual style variant for input fields */
    variant = 'brand subtle-outlined',
    /** @type {string} Extra class for every date and time input */
    input_class = '',
    /** @type {string | null} Minimum allowed date in ISO 8601 UTC datetime string */
    date_min = null,
    /** @type {string | null} Maximum allowed date in ISO 8601 UTC datetime string */
    date_max = null,
    /** @type {string | null} Minimum allowed time in HH:mm format */
    time_min = null,
    /** @type {string | null} Maximum allowed time in HH:mm format */
    time_max = null,
    /** @type {number} Time increment in seconds for time picker */
    step = 60,
    /** @type {string} class for view mode */
    view_class = 'text-body',
    class: css_class
  } = $props();

  let starts_at_local = $derived(localize_utc_date(starts_at ? new Date(starts_at) : new Date()));
  let ends_at_local = $derived(localize_utc_date(ends_at ? new Date(ends_at) : new Date()));
  let starts_at_local_yyyy_mm_dd = $derived(yyyy_mm_dd_from_date(starts_at_local));
  let ends_at_local_yyyy_mm_dd = $derived(yyyy_mm_dd_from_date(ends_at_local));
  let date_min_local = $derived(date_min ? localize_utc_date(new Date(date_min)) : undefined);
  let date_max_local = $derived(date_max ? localize_utc_date(new Date(date_max)) : undefined);
  let date_min_local_yyyy_mm_dd = $derived(date_min_local ? yyyy_mm_dd_from_date(date_min_local) : undefined);
  let date_max_local_yyyy_mm_dd = $derived(date_max_local ? yyyy_mm_dd_from_date(date_max_local) : undefined);

  // State for visual feedback
  let start_date_highlight = $state(false);
  let start_time_highlight = $state(false);
  let end_date_highlight = $state(false);
  let end_time_highlight = $state(false);
  let previous_gap_minutes = $state(60);

  // Initialize with defaults if needed
  $effect(() => {
    if (!starts_at || !ends_at) {
      const now = new Date();
      now.setMinutes(0, 0, 0); // Round to hour
      
      if (!starts_at) {
        starts_at = now.toISOString();
      }
      if (!ends_at) {
        const default_end = new Date(now);
        default_end.setUTCHours(default_end.getUTCHours() + 1);
        ends_at = default_end.toISOString();
      }
    }
  });

  // Helper to calculate minutes between dates
  function get_minutes_between(date1, date2) {
    return Math.round((date2.getTime() - date1.getTime()) / (1000 * 60));
  }

  function handle_start_date_change() {
    if (!starts_at || !ends_at) return;

    
    // Store current gap if valid
    if (ends_at_local > starts_at_local) {
      previous_gap_minutes = get_minutes_between(starts_at_local, ends_at_local);
    }

    // Only adjust if end is before or equal to start
    if (ends_at_local <= starts_at_local) {

      

      const same_day = starts_at_local_yyyy_mm_dd === ends_at_local_yyyy_mm_dd;
      const new_end = new Date(starts_at);
      
      if (same_day) {
        // Adjust end time forward
        new_end.setUTCMinutes(new_end.getUTCMinutes() + previous_gap_minutes);
        ends_at = new_end.toISOString();
        end_time_highlight = true;
        setTimeout(() => end_time_highlight = false, 1000);
      } else {
        // Adjust end date forward
        new_end.setUTCMinutes(new_end.getUTCMinutes() + previous_gap_minutes);
        ends_at = new_end.toISOString();
        end_date_highlight = true;
        setTimeout(() => end_date_highlight = false, 1000);
      }
    }
  }

  function handle_end_date_change() {
    if (!starts_at || !ends_at) return;

    
    // Store current gap if valid
    if (ends_at_local > starts_at_local) {
      previous_gap_minutes = get_minutes_between(starts_at_local, ends_at_local);
    }

    // Only adjust if end is before or equal to start
    if (ends_at_local <= starts_at_local) {

      const same_day = starts_at_local_yyyy_mm_dd === ends_at_local_yyyy_mm_dd;
      
      if (same_day) {
        // Adjust end time forward
        const new_end = new Date(ends_at);
        new_end.setUTCHours(starts_at_local.getUTCHours() + 1);
        ends_at = new_end.toISOString();
        end_time_highlight = true;
        setTimeout(() => end_time_highlight = false, 1000);
      } else {
        // Adjust start date backward
        const new_start = new Date(ends_at);
        new_start.setUTCMinutes(new_start.getUTCMinutes() - previous_gap_minutes);
        starts_at = new_start.toISOString();
        start_date_highlight = true;
        setTimeout(() => start_date_highlight = false, 1000);
      }
    }
  }

  function handle_start_time_change() {
    if (!starts_at || !ends_at) return;


    // Store current gap if valid
    if (ends_at_local > starts_at_local) {
      previous_gap_minutes = get_minutes_between(starts_at_local, ends_at_local);
    }

    // Only adjust end if necessary
    if (ends_at_local <= starts_at_local) {
      const new_end = new Date(starts_at);
      new_end.setUTCMinutes(new_end.getUTCMinutes() + previous_gap_minutes);
      ends_at = new_end.toISOString();
      end_time_highlight = true;
      setTimeout(() => end_time_highlight = false, 1000);
    }
  }

  function handle_end_time_change() {
    if (!starts_at || !ends_at) return;


    // Store current gap if valid
    if (ends_at_local > starts_at_local) {
      previous_gap_minutes = get_minutes_between(starts_at_local, ends_at_local);
    }

    // Only adjust start if necessary
    if (ends_at_local <= starts_at_local) {
      const new_start = new Date(ends_at);
      new_start.setUTCMinutes(new_start.getUTCMinutes() - previous_gap_minutes);
      starts_at = new_start.toISOString();
      start_time_highlight = true;
      setTimeout(() => start_time_highlight = false, 1000);
    }
  }

  // All-day toggle. Toggling on snaps the end TIME to end-of-day in the
  // business timezone but keeps the end DATE (multi-day events). The time
  // that was replaced is remembered so toggling off restores it instead of
  // leaving the 23:59 snap behind. Restoring reapplies the remembered wall
  // time to whatever end date is current, so a date changed while all-day
  // is kept, and falls back to the usual gap if the result would no longer
  // be after the start.
  let ends_at_before_all_day = null;

  function handle_all_day_change(event) {
    if (!starts_at) return;

    if (event.target.checked) {
      ends_at_before_all_day = ends_at || null;
      const reference = ends_at || starts_at;
      const local_date = localize_utc_date(new Date(reference));
      const end_of_day_local = get_end_of_day(local_date);
      ends_at = unlocalize_utc_date(end_of_day_local).toISOString();
      end_date_highlight = true;
      setTimeout(() => end_date_highlight = false, 1000);
      return;
    }

    if (!ends_at_before_all_day) return;
    const remembered_time = format_utc_time_as_local(ends_at_before_all_day);
    ends_at_before_all_day = null;
    let restored = new Date(convert_local_time_to_utc(remembered_time, ends_at || starts_at));
    if (restored <= new Date(starts_at)) {
      restored = new Date(starts_at);
      restored.setUTCMinutes(restored.getUTCMinutes() + previous_gap_minutes);
    }
    ends_at = restored.toISOString();
    end_time_highlight = true;
    setTimeout(() => end_time_highlight = false, 1000);
  }
</script>

{#if is_editing}
  <div class='datetime-range {css_class}'>
    <!-- Start: date and time -->
    <div class='form-row-narrow-label'>
      {#if show_label}
        <FormLabel for='starts_at'>
          {t('start_date')}
        </FormLabel>
      {/if}
      <div class='flex-row flex-wrap items-center gap-x gap-y g-3'>
        <DateInput
          {is_editing}
          bind:value={starts_at}
          name='starts_at'
          id='starts_at'
          {variant}
          {size}
          min={date_min_local_yyyy_mm_dd}
          max={date_max_local_yyyy_mm_dd}
          onchange={handle_start_date_change}
          class='fit-content {input_class} {start_date_highlight ? "highlight-change" : ""}'
        />
        {#if !all_day}
          <TimeInput
            {is_editing}
            bind:value={starts_at}
            name='starts_at_time'
            {variant}
            {size}
            {step}
            min={time_min}
            max={time_max}
            onchange={handle_start_time_change}
            class='fit-content {input_class} {start_time_highlight ? "highlight-change" : ""}'
          />
        {/if}
      </div>
    </div>

    <!-- End: date and time -->
    <div class='form-row-narrow-label'>
      {#if show_label}
        <FormLabel for='ends_at'>
          {t('end_date')}
        </FormLabel>
      {/if}
      <div class='flex-row flex-wrap items-center gap-x gap-y g-3'>
        <DateInput
          {is_editing}
          bind:value={ends_at}
          name='ends_at'
          id='ends_at'
          {variant}
          {size}
          min={starts_at ?
            // we use the later date of starts_at or date_min as the minimum allowed end date
            (starts_at_local > date_min_local ? starts_at_local_yyyy_mm_dd : date_min_local_yyyy_mm_dd)
            : date_min_local_yyyy_mm_dd
          }
          max={date_max_local_yyyy_mm_dd}
          class='fit-content {input_class} {end_date_highlight ? "highlight-change" : ""}'
          onchange={handle_end_date_change}
        />
        {#if !all_day}
          <TimeInput
            {is_editing}
            bind:value={ends_at}
            name='ends_at_time'
            {variant}
            {size}
            {step}
            min={
              // The time min is the time of the start date if the start and end are on the same day
              starts_at && starts_at_local_yyyy_mm_dd === ends_at_local_yyyy_mm_dd ?
              starts_at_local.toISOString().split('T')[1] :
              time_min}
            max={time_max}
            class='fit-content {input_class} {end_time_highlight ? "highlight-change" : ""}'
            onchange={handle_end_time_change}
          />
        {/if}
      </div>
    </div>

    <!-- All day: the checkbox carries its own words, no row label needed -->
    <label class='event-check text-control'>
      <input
        id='all_day'
        type='checkbox'
        bind:checked={all_day}
        onchange={handle_all_day_change}
      />
      {t('all_day')}
    </label>
  </div>
{:else}
  <p class='{view_class} leading-trim {css_class}'>
    {format_date_range(starts_at, ends_at, all_day)}
  </p>
{/if}

<style>
  :global(.highlight-change) {
    animation: highlight 1s ease-out;
  }

  @keyframes highlight {
    0% { background-color: var(--editing); }
    100% { background-color: transparent; }
  }
</style>